import { baseApi } from "../../../../app/api";
import { store } from "../../../../app/store";
import { selectLanguageInfo } from "../../../../common/features/language/languageSelectors";
import { transformProduct } from "../../product/helpers/productApiHelpers/transformProduct";

import type { RawProduct } from "../../../../types";
import type { TransformedProduct } from "../../product/types/productApiTypes";

const CUSTOM_REST_API_BASE =
  window.commonVariables?.customRestApiBase || "/wp-json/ecommerce-app/v1";

export const compareApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Retrieves the list of product IDs to compare.
     */
    getCompareList: builder.query<number[], void>({
      query: () => ({
        url: `${CUSTOM_REST_API_BASE}/compare`,
        method: "GET",
      }),
      providesTags: ["CompareList"],
    }),

    /**
     * Fetches detailed product data for the compare list.
     */
    getCompareItemsData: builder.query<TransformedProduct[], number[]>({
      query: (ids) => ({
        url: `${CUSTOM_REST_API_BASE}/products/batch`,
        params: { include: ids.join(",") },
      }),
      transformResponse: (response: RawProduct[]) => {
        if (!Array.isArray(response)) return [];
        const languageInfo = selectLanguageInfo(store.getState());
        return response
          .map((product) => transformProduct(product, languageInfo))
          .filter((item): item is TransformedProduct => item !== null);
      },
    }),

    /**
     * Toggles an item in the compare list.
     */
    toggleCompareItem: builder.mutation<number[], number>({
      query: (productId) => ({
        url: `${CUSTOM_REST_API_BASE}/compare/toggle`,
        method: "POST",
        body: { id: productId },
      }),
      // Note: Backend now returns 'compare_limit_reached' error which useMutationWithFeedback catches.
      invalidatesTags: ["CompareList"],
    }),

    /**
     * Explicitly remove an item.
     */
    removeCompareItem: builder.mutation<number[], number>({
      query: (productId) => ({
        url: `${CUSTOM_REST_API_BASE}/compare/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CompareList"],
    }),
  }),
});

export const {
  useGetCompareListQuery,
  useGetCompareItemsDataQuery,
  useToggleCompareItemMutation,
  useRemoveCompareItemMutation,
} = compareApi;
